import { useSnackbar } from 'notistack';
import { useState, useEffect } from 'react';

import CustomModal from '@/components/CustomModal';
import { RadioButtonGroup } from '@/components/RadioButton';
import Select from '@/components/Select';
import { STATUS_OPTIONS } from '@/constants/constant';
import { ERoleAccess } from '@/enum';
import useAccess from '@/hooks/useAccess';
import { useFormatText } from '@/hooks/useFormatText';
import { getRoleDropdown } from '@/service/adminServices';
import { IAdminFilterValues } from '@/types';

const AdminFilterModal = ({
  setIsOpen,
  onApply,
  initialValues = {},
}: {
  setIsOpen: (open: boolean) => void;
  onApply: (filters: IAdminFilterValues) => void;
  initialValues?: Partial<IAdminFilterValues>;
}) => {
  const { hasAccess } = useAccess();

  const { formatText } = useFormatText();
  const { enqueueSnackbar } = useSnackbar();
  const [status, setStatus] = useState(initialValues.status || '');
  const [roles, setRoles] = useState<string[]>(initialValues.roles || []);
  const [roleOptions, setRoleOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);

  useEffect(() => {
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await getRoleDropdown();
      if (response?.status && response?.data) {
        const formattedRoles = response.data?.map(
          (role: { id: string; name: string }) => ({
            value: role.id,
            label: role.name,
          })
        );
        setRoleOptions(formattedRoles);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      enqueueSnackbar('Failed to load roles', { variant: 'error' });
    }
  };

  const handleClear = () => {
    setStatus('');
    setRoles([]);
    onApply({ status: '', roles: [] });
    setIsOpen(false);
  };

  const handleApply = () => {
    const filters = { status, roles };
    onApply(filters);
    setIsOpen(false);
  };

  if (!hasAccess(ERoleAccess.USER_MANAGEMENT)) return null;

  return (
    <CustomModal
      setIsOpen={setIsOpen}
      isHeader
      isFooter
      heading={formatText('common.actions.filter')}
      position="right"
      className="max-w-150"
      confirmButtonProps={{
        label: formatText('common.actions.show-results'),
        variant: 'primary',
        onClick: handleApply,
        disabled: !status && roles.length === 0,
      }}
      cancelButtonProps={{
        label: formatText('common.actions.clear-filters'),
        desructive: true,
        onClick: handleClear,
        className: 'text-sm underline px-0!',
      }}
    >
      <div className="flex flex-col gap-6 w-full">
        {/* Status */}
        <RadioButtonGroup
          name="status"
          label={formatText('user-management.status')}
          options={STATUS_OPTIONS}
          value={status}
          onChange={setStatus}
          formatLabel={formatText}
        />

        {/* Roles Multi-Select */}
        <Select
          name={formatText('user-management.admin.roles')}
          options={roleOptions}
          selected={roles}
          onChange={(value) => setRoles(Array.isArray(value) ? value : [])}
          placeholder={formatText('user-management.admin.select-roles')}
          isMulti={true}
          isClearable={true}
          isTextFormat={true}
          className="min-h-13"
        />
      </div>
    </CustomModal>
  );
};

export default AdminFilterModal;
